// API Configuration.
//
// URLs are HOST-RELATIVE by default. The .htaccess at the repo root
// rewrites /api/* to backend/public/index.php and /storage/* to
// backend/public/storage/, so paths that begin with `/` resolve against
// whatever host the SPA is currently being served from. This is what
// makes the same compiled bundle work on the build server, on any
// installer clone, and in a self-hosted production deploy — no per-host
// rebuild required.
//
// For local dev (`npm start`) with no backend on localhost, CRA loads
// .env.development and these fall back to REACT_APP_API_BASE_URL /
// REACT_APP_STORAGE_BASE_URL instead, pointing the SPA at a real remote
// backend. `npm run build` does not load .env.development, so production
// builds keep the relative-path behavior untouched.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const STORAGE_BASE_URL = process.env.REACT_APP_STORAGE_BASE_URL || '/storage';

const API_CONFIG = {
  BASE_URL: API_BASE_URL,

  // API endpoints
  ENDPOINTS: {
    // Authentication
    LOGIN: '/login',
    REGISTER: '/register',
    LOGOUT: '/logout',
    REFRESH_TOKEN: '/refresh-token',
    CSRF_TOKEN: '/csrf-token',
    
    // Posts
    POSTS: '/posts',
    UPLOAD_POST: '/upload-post',
    FETCH_POSTS: '/posts',
    
    // Polls and Quizzes
    VOTE_POLL: '/polls/vote',
    SUBMIT_QUIZ: '/quizzes/submit',
    
    // Files
    UPLOAD_FILE: '/upload-file',
    DELETE_FILE: '/delete-file',
    UPLOAD_THUMBNAIL: '/upload-thumbnail',
    
    // User
    USER_PROFILE: '/user/profile',
    USER_MEDIA: '/user-media',
    
    // Admin
    ADMIN_USERS: '/admin/users',
    
    // Webhooks
    STRIPE_WEBHOOK: '/webhook/stripe',
    NOWPAYMENTS_WEBHOOK: '/webhook/nowpayments'
  },
  
  // File URLs — derived from the same base URLs as above, for the same reason.
  FILE_URLS: {
    STORAGE_BASE:      STORAGE_BASE_URL,
    PLACEHOLDER_IMAGE: `${API_BASE_URL}/placeholder`,
    DEFAULT_AVATAR:    `${STORAGE_BASE_URL}/default-avatar.png`,
  },
  
  // Environment settings
  ENVIRONMENT: process.env.NODE_ENV || 'development',
  
  // Timeout settings
  TIMEOUT: {
    DEFAULT: 30000,  // 30 seconds
    UPLOAD: 300000,  // 5 minutes for file uploads (video can be up to 100MB)
    POST: 30000      // 30 seconds for post creation
  }
};

// Helper functions
export const getApiUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

export const getStorageUrl = (filePath, disk = 'public') => {
  if (!filePath) return null;

  // If it's already a full URL, return as is
  if (filePath.startsWith('http')) {
    return filePath;
  }

  // Handle different storage disks
  // For S3 or other cloud storage, the URL should already be full
  // For local/public disk, use the storage base URL
  if (disk === 's3' || disk === 's3_private' || disk === 'cloud' || disk === 'spaces' || disk === 'b2') {
    // Cloud URLs should be returned from backend as full signed URLs
    // If we get here with a relative path, still try storage base
    return filePath.startsWith('/')
      ? `${API_CONFIG.FILE_URLS.STORAGE_BASE}${filePath}`
      : `${API_CONFIG.FILE_URLS.STORAGE_BASE}/${filePath}`;
  }

  // Default: local or public disk
  if (filePath.startsWith('/')) {
    return `${API_CONFIG.FILE_URLS.STORAGE_BASE}${filePath}`;
  }

  // If it's just a filename, prepend storage base URL
  return `${API_CONFIG.FILE_URLS.STORAGE_BASE}/${filePath}`;
};

export const getPlaceholderImage = (width = 24, height = 24) => {
  return `${API_CONFIG.FILE_URLS.PLACEHOLDER_IMAGE}/${width}/${height}`;
};

export const getDefaultAvatar = () => {
  return API_CONFIG.FILE_URLS.DEFAULT_AVATAR;
};

export { API_CONFIG };
export default API_CONFIG;
